import { useState, useEffect } from 'react';
import { 
  Stethoscope, Phone, Mail, MapPin, Building2, 
  UserPlus, Trash2, AlertCircle, ChevronRight,
  Shield, MessageSquare, Calendar, MoreVertical
} from 'lucide-react';
import api from "../api/client";
import DoctorAssignmentOnboarding from './DoctorAssignmentOnboarding';

/**
 * Care Team Display Component
 * Shows patient's assigned doctors and care providers
 * Allows requesting new doctors or removing existing ones
 */
const CareTeamDisplay = ({ patientId }) => {
  const [careTeam, setCareTeam] = useState([]);
  const [primaryProvider, setPrimaryProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(null);

  useEffect(() => {
    loadCareTeam();
  }, [patientId]);

  const loadCareTeam = async () => {
    try {
      setLoading(true);
      const response = await api.get('/patient-profile/my-care-team');
      const data = response.data?.data || {};
      
      setPrimaryProvider(data.primary || null);
      setCareTeam(data.members || []);
    } catch (err) {
      console.error('Failed to load care team:', err);
      setError('Failed to load care team information');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveDoctor = async (doctorId) => {
    try {
      await api.post(`/patient-profile/leave-doctor/${doctorId}`);
      // Reload care team
      loadCareTeam();
      setShowLeaveConfirm(null);
    } catch (err) {
      console.error('Failed to leave doctor:', err);
      setError('Failed to remove doctor from care team');
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    loadCareTeam();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-20 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
        <button 
          onClick={loadCareTeam}
          className="mt-2 text-sm text-red-600 hover:text-red-800"
        >
          Try again
        </button>
      </div>
    );
  }

  // Show onboarding if no doctor assigned
  if (showOnboarding) {
    return (
      <DoctorAssignmentOnboarding 
        patientId={patientId}
        onComplete={handleOnboardingComplete}
        onSkip={() => setShowOnboarding(false)}
      />
    );
  }

  const hasPrimaryProvider = primaryProvider || careTeam.find(m => m.role === 'primary');

  return (
    <div className="space-y-4">
      {/* Primary Provider Card */}
      {hasPrimaryProvider && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                <Stethoscope className="w-7 h-7 text-blue-600" />
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-slate-900">
                    Dr. {hasPrimaryProvider.name || hasPrimaryProvider.providerId?.name}
                  </h3>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                    Primary
                  </span>
                </div>
                
                <p className="text-slate-600 text-sm">
                  {hasPrimaryProvider.specialization || hasPrimaryProvider.providerId?.specialization}
                </p>
                
                {(hasPrimaryProvider.institution || hasPrimaryProvider.providerId?.institution) && (
                  <p className="text-slate-500 text-sm mt-0.5 flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" />
                    {hasPrimaryProvider.institution || hasPrimaryProvider.providerId?.institution}
                  </p>
                )}

                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  {(hasPrimaryProvider.phone || hasPrimaryProvider.providerId?.phone) && (
                    <a 
                      href={`tel:${hasPrimaryProvider.phone || hasPrimaryProvider.providerId?.phone}`}
                      className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600"
                    >
                      <Phone className="w-4 h-4" />
                      {hasPrimaryProvider.phone || hasPrimaryProvider.providerId?.phone}
                    </a>
                  )}
                  
                  <a 
                    href={`mailto:${hasPrimaryProvider.email || hasPrimaryProvider.providerId?.email}`}
                    className="flex items-center gap-1.5 text-slate-600 hover:text-blue-600"
                  >
                    <Mail className="w-4 h-4" />
                    Message
                  </a>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowLeaveConfirm(hasPrimaryProvider)}
              className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-white/50"
              title="Leave care team"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          {/* Data Sharing Status */}
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Shield className="w-4 h-4 text-green-600" />
                <span>Data sharing active</span>
              </div>
              <span className="text-xs text-slate-500">
                Your doctor can see your symptoms, conditions, and medications
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Care Team List */}
      {careTeam.length > 1 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
            <h4 className="font-medium text-slate-700">Care Team</h4>
          </div>
          
          <div className="divide-y divide-slate-100">
            {careTeam.filter(m => m.role !== 'primary').map((member) => (
              <div key={member._id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <Stethoscope className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      Dr. {member.providerId?.name}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span>{member.providerId?.specialization}</span>
                      <span className="text-slate-300">•</span>
                      <span className="capitalize">{member.role}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowLeaveConfirm(member)}
                  className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Doctor State */}
      {!hasPrimaryProvider && !showOnboarding && (
        <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Stethoscope className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-medium text-slate-900 mb-2">No Doctor Assigned</h3>
          <p className="text-slate-600 text-sm mb-4 max-w-sm mx-auto">
            Having a doctor assigned allows you to receive personalized care and responses to your health reports.
          </p>
          <button
            onClick={() => setShowOnboarding(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            <UserPlus className="w-5 h-5 inline mr-2" />
            Find a Doctor
          </button>
        </div>
      )}

      {/* Add Another Doctor */}
      {hasPrimaryProvider && (
        <button
          onClick={() => setShowOnboarding(true)}
          className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 
                     hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all
                     flex items-center justify-center gap-2"
        >
          <UserPlus className="w-5 h-5" />
          Add Another Doctor
        </button>
      )}

      {/* Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Leave Care Team?
            </h3>
            <p className="text-slate-600 mb-4">
              Are you sure you want to remove Dr. {showLeaveConfirm.name || showLeaveConfirm.providerId?.name} from your care team? 
              They will no longer have access to your health data.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> If this is your only doctor, you won't receive personalized care responses until you assign a new one.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveConfirm(null)}
                className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-medium
                           hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleLeaveDoctor(
                  showLeaveConfirm._id || showLeaveConfirm.providerId?._id || showLeaveConfirm.id
                )}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
              >
                Leave Care Team
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareTeamDisplay;
